import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getQuestions, getQuestion, createQuestion, updateQuestion, deleteQuestion,
  toggleQuestionActive, importQuestionsExcel, downloadQuestionTemplate,
  getDimensions, getQuestionMeta, bulkDeleteQuestions,
} from '../../api/admin';
import AdminLayout from '../../components/AdminLayout';
import Button from '../../components/Button';
import Alert from '../../components/Alert';

const TYPE_LABEL = { ability: '能力题', trait: '素质题' };
const OPTION_LETTERS = ['A', 'B', 'C', 'D'];

const emptyOptions = () => OPTION_LETTERS.map(letter => ({
  letter, text: '', main_score: 0,
}));
const EMPTY_FORM = {
  question_type: 'ability',
  dimension: '', trait: '',
  stem: '', scenario_text: '', scenario_tag: '',
  options: emptyOptions(),
  is_active: true,
};

// 4 选项主分必须恰好是 {1,2,3,4} 的排列（与后端 / Excel 导入同口径）
function scoresAreValid(options) {
  const scores = options.map(o => Number(o.main_score)).filter(s => s >= 1 && s <= 4);
  if (scores.length !== 4) return false;
  return scores.slice().sort().join(',') === '1,2,3,4';
}

export default function AdminQuestionsPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [traits, setTraits] = useState([]);
  const [scenarioTags, setScenarioTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterDim, setFilterDim] = useState('');
  const [filterTrait, setFilterTrait] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 20;

  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const [selected, setSelected] = useState(new Set());
  const allChecked = questions.length > 0 && questions.every(q => selected.has(q.id));
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(questions.map(q => q.id)));
  const toggleOne = (id) => setSelected(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  const tagName = (code) => {
    const hit = scenarioTags.find(t => t.code === code);
    return hit ? hit.name : (code || '—');
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (filterType) params.question_type = filterType;
      if (filterDim) params.dimension = filterDim;
      if (filterTrait) params.trait = filterTrait;
      const { data } = await getQuestions(params);
      setQuestions(data.results ?? data);
      setTotal(data.count ?? (data.results ?? data).length);
    } catch { setError('加载失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page, filterType, filterDim, filterTrait]); // eslint-disable-line
  useEffect(() => {
    getDimensions().then(({ data }) => setDimensions(data.results ?? data)).catch(() => {});
    getQuestionMeta()
      .then(({ data }) => {
        setTraits(data.traits || []);
        setScenarioTags(data.scenario_tags || []);
      })
      .catch(() => {});
  }, []);

  const openCreate = () => { setEditItem(null); setForm(EMPTY_FORM); setError(''); setShowModal(true); };

  const openEdit = async (q) => {
    setError('');
    try {
      const { data } = await getQuestion(q.id);
      const opts = Array.isArray(data.options) && data.options.length === 4
        ? data.options.map((o, i) => ({
            letter: o.letter || OPTION_LETTERS[i],
            text: o.text || '',
            main_score: o.main_score || 0,
          }))
        : emptyOptions();
      setEditItem(data);
      setForm({
        question_type: data.question_type,
        dimension: data.dimension || '',
        trait: data.trait || '',
        stem: data.stem || '',
        scenario_text: data.scenario_text || '',
        scenario_tag: data.scenario_tag || '',
        options: opts,
        is_active: data.is_active ?? true,
      });
      setShowModal(true);
    } catch {
      setError('加载题目详情失败，请重试');
    }
  };

  const setOption = (idx, field, value) => {
    setForm(f => {
      const options = f.options.slice();
      options[idx] = { ...options[idx], [field]: value };
      return { ...f, options };
    });
  };

  const validateForm = () => {
    if (!form.stem.trim()) return '题干不能为空';
    if (!form.scenario_tag) return '请选择场景标签';
    if (form.question_type === 'ability' && !form.dimension) return '能力题必须选择所属能力';
    if (form.question_type === 'trait' && !form.trait) return '素质题必须选择所属素质';
    if (form.options.some(o => !o.text.trim())) return '4 个选项文案都必须填写';
    if (!scoresAreValid(form.options)) return '4 个选项主分必须是 1/2/3/4 各一个';
    return '';
  };

  const handleSave = async () => {
    const msg = validateForm();
    if (msg) { setError(msg); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        question_type: form.question_type,
        stem: form.stem.trim(),
        scenario_text: form.scenario_text.trim(),
        scenario_tag: form.scenario_tag,
        dimension: form.question_type === 'ability' ? (form.dimension || null) : null,
        trait: form.question_type === 'trait' ? (form.trait || null) : null,
        options: form.options.map(o => ({
          letter: o.letter,
          text: o.text.trim(),
          main_score: Number(o.main_score),
        })),
        is_active: form.is_active,
      };
      if (editItem) await updateQuestion(editItem.id, payload);
      else await createQuestion(payload);
      setSuccess(editItem ? '修改成功' : '新增成功');
      setShowModal(false);
      load();
    } catch (e) {
      const data = e.response?.data;
      setError(data?.detail || (data ? JSON.stringify(data) : '保存失败'));
    } finally { setSaving(false); }
  };

  const handleDelete = async (q) => {
    if (!window.confirm(`确认删除题目：${q.stem.slice(0, 30)}？`)) return;
    try { await deleteQuestion(q.id); setSuccess('已删除'); load(); }
    catch { setError('删除失败'); }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`确认删除选中的 ${selected.size} 道题目？`)) return;
    try {
      const { data } = await bulkDeleteQuestions([...selected]);
      setSuccess(`已删除 ${data.deleted} 道题目`);
      setSelected(new Set());
      load();
    } catch { setError('批量删除失败'); }
  };

  const handleToggle = async (q) => {
    try { await toggleQuestionActive(q.id); load(); }
    catch { setError('操作失败'); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const { data } = await downloadQuestionTemplate();
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'AIQ题库导入模版.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('模版下载失败，请重试');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const { data } = await importQuestionsExcel(file);
      if (data.errors && data.errors.length > 0) {
        setError(`${data.message}；错误详情：${data.errors.slice(0, 3).join('；')}${data.errors.length > 3 ? `…等 ${data.errors.length} 条` : ''}`);
      } else {
        setSuccess(data.message || '导入完成');
      }
      if (data.created > 0) load();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.message || '导入失败');
    } finally { e.target.value = ''; }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const formScoresValid = scoresAreValid(form.options);

  return (
    <AdminLayout title="题库管理">
      {error && <Alert type="error" style={{ marginBottom: 12 }}>{error}</Alert>}
      {success && <Alert type="success" style={{ marginBottom: 12 }}>{success}</Alert>}

      {/* 工具栏 */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (setPage(1), load())}
          placeholder="搜索题干 / 场景标签… (回车)"
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, minWidth: 220 }}
        />
        <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }}
          style={selectStyle}>
          <option value="">全部题型</option>
          <option value="ability">能力题</option>
          <option value="trait">素质题</option>
        </select>
        <select value={filterDim} onChange={e => { setFilterDim(e.target.value); setPage(1); }}
          style={selectStyle}>
          <option value="">全部能力</option>
          {dimensions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={filterTrait} onChange={e => { setFilterTrait(e.target.value); setPage(1); }}
          style={selectStyle}>
          <option value="">全部素质</option>
          {traits.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {selected.size > 0 && (
            <Button size="sm" variant="danger" onClick={handleBulkDelete}>
              删除所选 ({selected.size})
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={handleDownloadTemplate}>下载模版</Button>
          <Button size="sm" variant="secondary" onClick={() => fileRef.current.click()}>Excel 导入</Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImport} />
          <Button size="sm" variant="secondary" onClick={() => navigate('/admin/recycle-bin')}>回收站</Button>
          <Button size="sm" onClick={openCreate}>+ 新增题目</Button>
        </div>
      </div>

      {/* 表格 */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ padding: '12px 14px', borderBottom: '1px solid #e2e8f0', width: 36 }}>
                <input type="checkbox" checked={allChecked} onChange={toggleAll} />
              </th>
              {['序号', '题干', '题型', '归属', '场景标签', '来源', '状态', '操作'].map(h => (
                <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>加载中...</td></tr>
            ) : questions.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>暂无数据</td></tr>
            ) : questions.map((q, index) => (
              <tr key={q.id} style={{ borderBottom: '1px solid #f1f5f9', background: selected.has(q.id) ? '#eff6ff' : undefined }}>
                <td style={{ padding: '10px 14px' }}>
                  <input type="checkbox" checked={selected.has(q.id)} onChange={() => toggleOne(q.id)} />
                </td>
                <td style={{ padding: '10px 14px', color: '#94a3b8' }}>{(page - 1) * PAGE_SIZE + index + 1}</td>
                <td style={{ padding: '10px 14px', maxWidth: 340 }}>
                  <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {q.stem}
                  </span>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    background: q.question_type === 'ability' ? '#eff6ff' : '#fdf4ff',
                    color: q.question_type === 'ability' ? '#2563eb' : '#a21caf',
                  }}>
                    {TYPE_LABEL[q.question_type] || q.question_type}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', color: '#64748b' }}>
                  {q.dimension_name || q.trait_name || '—'}
                </td>
                <td style={{ padding: '10px 14px', color: '#64748b' }}>{tagName(q.scenario_tag)}</td>
                <td style={{ padding: '10px 14px' }}>
                  {q.is_ai_generated
                    ? <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#ede9fe', color: '#7c3aed' }}>AI</span>
                    : <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#f1f5f9', color: '#64748b' }}>题库</span>
                  }
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    background: q.is_active ? '#f0fdf4' : '#fef2f2',
                    color: q.is_active ? '#16a34a' : '#dc2626',
                  }}>
                    {q.is_active ? '启用' : '停用'}
                  </span>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => openEdit(q)} style={btnStyle('#2563eb')}>编辑</button>
                    <button onClick={() => handleToggle(q)} style={btnStyle(q.is_active ? '#d97706' : '#16a34a')}>
                      {q.is_active ? '停用' : '启用'}
                    </button>
                    <button onClick={() => handleDelete(q)} style={btnStyle('#dc2626')}>删除</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 分页 */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pageBtn(page > 1)}>上一页</button>
          <span style={{ padding: '6px 14px', fontSize: 13, color: '#64748b' }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pageBtn(page < totalPages)}>下一页</button>
        </div>
      )}

      {/* 新增/编辑弹窗 */}
      {showModal && (
        <Modal title={editItem ? '编辑题目' : '新增题目'} onClose={() => setShowModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="题型 *">
                <select
                  value={form.question_type}
                  onChange={e => setForm(f => ({ ...f, question_type: e.target.value, dimension: '', trait: '' }))}
                  style={inputStyle}>
                  <option value="ability">能力题</option>
                  <option value="trait">素质题</option>
                </select>
              </Field>
              {form.question_type === 'ability' ? (
                <Field label="所属能力 *">
                  <select value={form.dimension} onChange={e => setForm(f => ({ ...f, dimension: e.target.value }))} style={inputStyle}>
                    <option value="">— 请选择 —</option>
                    {dimensions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </Field>
              ) : (
                <Field label="所属素质 *">
                  <select value={form.trait} onChange={e => setForm(f => ({ ...f, trait: e.target.value }))} style={inputStyle}>
                    <option value="">— 请选择 —</option>
                    {traits.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </Field>
              )}
            </div>

            <Field label="题干 *（主问句）">
              <textarea value={form.stem} onChange={e => setForm(f => ({ ...f, stem: e.target.value }))}
                rows={2} style={inputStyle} />
            </Field>

            <Field label="情境描述（可选；运营整句出题时合并进题干即可）">
              <textarea value={form.scenario_text} onChange={e => setForm(f => ({ ...f, scenario_text: e.target.value }))}
                rows={2} style={inputStyle} />
            </Field>

            <Field label="场景标签 *">
              <select value={form.scenario_tag} onChange={e => setForm(f => ({ ...f, scenario_tag: e.target.value }))} style={inputStyle}>
                <option value="">— 请选择 —</option>
                {scenarioTags.map(t => <option key={t.code} value={t.code}>{t.name}（{t.code}）</option>)}
              </select>
            </Field>

            <Field label="四选项（文案 + 主分；4 个主分须为 1/2/3/4 各一个）">
              {form.options.map((opt, i) => (
                <div key={opt.letter} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                  <span style={{ width: 18, fontWeight: 700, color: '#475569', paddingTop: 8 }}>{opt.letter}</span>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input value={opt.text} placeholder={`选项 ${opt.letter} 文案`}
                      onChange={e => setOption(i, 'text', e.target.value)} style={inputStyle} />
                  </div>
                  <select value={opt.main_score} onChange={e => setOption(i, 'main_score', Number(e.target.value))}
                    style={{ ...inputStyle, width: 70 }}>
                    <option value={0}>主分</option>
                    {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              ))}
              <div style={{ fontSize: 12, color: formScoresValid ? '#16a34a' : '#dc2626', marginTop: 2 }}>
                {formScoresValid ? '✓ 主分组合合法' : '主分必须是 1 / 2 / 3 / 4 各一个，不能重复或缺漏'}
              </div>
            </Field>

            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={form.is_active}
                onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
              启用（参与抽题）
            </label>

            {error && <Alert type="error">{error}</Alert>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
              <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>取消</Button>
              <Button size="sm" onClick={handleSave} loading={saving}>保存</Button>
            </div>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}

const selectStyle = { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 };
const btnStyle = (color) => ({
  padding: '4px 10px', borderRadius: 6, border: `1px solid ${color}30`,
  background: `${color}0f`, color, fontSize: 12, cursor: 'pointer', fontWeight: 500,
});
const pageBtn = (active) => ({
  padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0',
  background: active ? '#fff' : '#f8fafc', color: active ? '#2563eb' : '#94a3b8',
  cursor: active ? 'pointer' : 'not-allowed', fontSize: 13,
});
const inputStyle = { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, width: '100%', boxSizing: 'border-box' };

function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 640, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
